<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { pmaSvg, growSvg } from '@/util/icons'
import { capitalize, escapeHtml } from '@/util/strings'
import { useTooltipHoverFocusSync } from '@/util/tooltip'
import { useVisibilityStore } from '@/visibility/visibility.store'

const props = defineProps({
  goal_resources: {
    type: Array<string>,
    required: false,
    default: () => {
      return []
    }
  },
  resource_languages: {
    type: Array<string>,
    required: false,
    default: () => {
      return []
    }
  },
  show_grow: {
    type: Boolean,
    required: false,
    default: false
  },
  has_progress_monitoring: {
    type: Boolean,
    required: false,
    default: false
  }
})
const visibilityStore = useVisibilityStore()
const { betaVisibility } = storeToRefs(visibilityStore)
const { onMouseEnter, onMouseLeave, onFocus, onBlur, onClick, reset } = useTooltipHoverFocusSync()

const showProgress = computed(() => {
  return props.has_progress_monitoring || props.goal_resources.length > 0
})
const hasNonEnglishLanguages = computed(() => {
  return props.resource_languages.some((lang: string) => lang != 'english')
})

const iconLabel = computed(() => {
  let labelPieces = []
  if (hasNonEnglishLanguages.value) {
    labelPieces.push('Has assessments in multiple languages, including ' + formattedLanguages.value + '.')
  }
  if (showProgress.value) {
    labelPieces.push('Has progress monitoring assessments and data sheets.')
  }
  if (props.show_grow && betaVisibility.value) {
    labelPieces.push('This goal can be tracked in Goalbook Grow if saved to the Library.')
  }
  return labelPieces.join(' ')
})

const progressTooltip = computed(() => {
  return `<div class='flex items-start'><span class="pr-2">${pmaSvg}</span> <p>Progress monitoring assessments and data sheets</p></div>`
})
const progressAndLanguageTooltip = computed(() => {
  return `<div class='flex items-start pt-2'><span class="material-icons-round pr-1 text-lg"> public </span> <p>Includes assessments in <b>${escapeHtml(formattedLanguages.value)}</b></p></div>`
})
const growTooltip = computed(() => {
  return `<div class='tooltip-inner-container flex align-center pt-2 items-start'><span class='w-6 mr-1'>${growSvg}</span> <p>Can be tracked in Goalbook Grow if saved to the Library</p></div>`
})

const getTooltipContent = computed(() => {
  let tooltipContent = ''
  if (showProgress.value) {
    tooltipContent += progressTooltip.value
  }
  if (hasNonEnglishLanguages.value) {
    tooltipContent += progressAndLanguageTooltip.value
  }
  if (props.show_grow && betaVisibility.value) {
    tooltipContent += growTooltip.value
  }
  return tooltipContent
})

const formattedLanguages = computed(() => {
  return props.resource_languages
    ?.reduce((filtered: string[], lang) => {
      if (lang !== 'english') {
        filtered.push(capitalize(lang))
      }
      return filtered
    }, [])
    .join(', ')
})

// This trigger has no `.focus` modifier, so PrimeVue's Tooltip directive binds
// only mouseenter/mouseleave/click (see primevue/tooltip's bindEvents) — never
// focus/blur. Its own Escape handler is real (bound to the trigger element),
// but a real Escape keypress is dispatched to document.activeElement, and a
// hover-only trigger is never the active element. Re-dispatching the keydown
// directly at the trigger reaches that same handler regardless of true focus,
// satisfying WCAG 1.4.13's Dismissible criterion for a hover-triggered tooltip.
const tooltipTrigger = ref<HTMLElement | null>(null)

// `v-if` on this template's root can remove and later recreate the trigger
// without firing blur on the old one, so a stale isFocused/isHovering from
// the removed trigger must not survive onto the new one.
watch(tooltipTrigger, (trigger, previousTrigger) => {
  if (trigger !== previousTrigger) reset()
})

const dismissTooltipOnEscape = (event: KeyboardEvent) => {
  if (event.key !== 'Escape') return
  tooltipTrigger.value?.dispatchEvent(new KeyboardEvent('keydown', { code: 'Escape' }))
}

onMounted(() => document.addEventListener('keydown', dismissTooltipOnEscape))
onUnmounted(() => document.removeEventListener('keydown', dismissTooltipOnEscape))
</script>
<template>
  <div
    ref="tooltipTrigger"
    class="flex items-center justify-between"
    v-if="showProgress || hasNonEnglishLanguages || (props.show_grow && betaVisibility)"
    role="img"
    tabindex="0"
    v-tooltip.bottom="{
      value: getTooltipContent,
      escape: false,
      autoHide: false,
    }"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    @focus="onFocus"
    @blur="onBlur"
    @click="onClick"
    :aria-label="iconLabel"
  >
    <span v-if="hasNonEnglishLanguages" class="material-icons-round pr-1 text-lg"> public </span>
    <span v-if="showProgress" v-html="pmaSvg" />
    <span v-if="props.show_grow && betaVisibility" v-html="growSvg" class="w-4 ml-[5px] -mr-2"/>
  </div>
</template>
<style>
.tooltip-inner-container {
  svg {
    width: 15px;
  }
}
</style>